import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import CrudList from "./CrudList";
import CrudForm from "./CrudForm";
import CompositeLayout from "./CompositeLayout";
import { callApi, createGql, ErrorAlert, objectsAreEqual, setPropValue, getPropValue, urlStringToPk, getAllFormColumns } from './utils';
import useApp from '../AppContext';

/*
export async function loader({ params }) {
    const layoutId = params.layoutId;
    return layoutId;
}
*/
export function DataComponentWrapper(props) {
    const app = useApp();
    const params = useParams();
    const [data, setData] = useState({});
    const [rowsRestrictions, setRowsRestrictions] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [objectMeta, setObjectMeta] = useState({});
    const [objUnderEdit, setObjUnderEdit] = useState({});
    const [changedData, setChangedData] = useState({});
    const [loadDataAfterInit, setLoadDataAfterInit] = useState(null);
    const [beginEditAfterLoad, setBeginEditAfterLoad] = useState(null);
    const [saveAfterEndEdit, setSaveAfterEndEdit] = useState(null);
    const [beginEditAfterSave, setBeginEditAfterSave] = useState(null);
    const [beginEditAfterEdit, setBeginEditAfterEdit] = useState(null);
    const [serverUrl] = useState(app.serverUrl);
    const [gridTop] = useState(app.gridTop);
    const [layout, setLayout] = useState(null);
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(null);
    const [component, setComponent] = useState(null);
    const language = app.sessionToken?.Language;
    // useEffect(() => {
    //     setData({});
    //     setRowsRestrictions({})
    //     setOriginalData({});
    //     setObjectMeta({});
    //     setObjUnderEdit({});
    //     setChangedData({});
    //     setLoadDataAfterInit(null);
    //     setBeginEditAfterLoad(null);
    //     setSaveAfterEndEdit(null);
    //     setBeginEditAfterSave(null);
    // }, [params]);
    const [prevLayoutId, setPrevLayoutId] = useState(null);

    useEffect(() => {
        const currentLayoutId = params.layoutId || props.layoutId;
        if (currentLayoutId !== prevLayoutId) {
            setData({});
            setRowsRestrictions({})
            setOriginalData({});
            setObjectMeta({});
            setObjUnderEdit({});
            setChangedData({});
            setLoadDataAfterInit(null);
            setBeginEditAfterLoad(null);
            setSaveAfterEndEdit(null);
            setBeginEditAfterSave(null);
            setPrevLayoutId(currentLayoutId);
        }
    }, [params.layoutId, props.layoutId, prevLayoutId]);

    /*
    useEffect(() => {
        if (props.onObjUnderEditChanged) {
            props.onObjUnderEditChanged(objUnderEdit);
        }
    }, [props.onObjUnderEditChanged, objUnderEdit]);
    */
    const { onObjUnderEditChanged } = props;
    useEffect(() => {
        if (onObjUnderEditChanged) {
            onObjUnderEditChanged(objUnderEdit);
        }
    }, [onObjUnderEditChanged, objUnderEdit]);


    const createPkPrmDefsAndVals = useCallback((obj, pkColNames) => {
        const defs = pkColNames.map((pkColName) => { return { parameterName: pkColName } });
        const vals = {}
        pkColNames.forEach((pkColName) => { vals[pkColName] = obj[pkColName] });
        for (let i = 0; i < pkColNames.length; i++) {
            const pkColName = pkColNames[i];
            vals[pkColName] = obj[pkColName];
        }
        return {
            defs,
            vals
        }
    }, []);

    const initObjectMeta = useCallback(async (objectName, instanceName, listLayout, formLayout, shouldLoadDataAfterInit) => {
        let pkColumnNames = [];
        let editModeColumnNames = [];
        if (listLayout) {
            pkColumnNames = listLayout.primaryKey;
            const listColNames = listLayout.columns.filter(col => col.controlType !== "List").map(col => col.columnName);
            editModeColumnNames = [...editModeColumnNames, ...listColNames];
            editModeColumnNames = [...editModeColumnNames, ...listLayout.primaryKey.filter(a => !editModeColumnNames.includes(a))];
            editModeColumnNames = [...editModeColumnNames, ...listLayout.additionalColumns.filter(a => !editModeColumnNames.includes(a))];

        } else if (formLayout) {
            pkColumnNames = formLayout.primaryKey;
        }
        if (!formLayout && listLayout.editFormUrl) {
            let url = `${serverUrl}/${listLayout.editFormUrl}`;
            if (instanceName) url += `?instanceName=${instanceName}`;
            formLayout = await callApi('GET', url, props.userToken);
        }
        if (formLayout) {
            editModeColumnNames = getAllFormColumns(formLayout, editModeColumnNames);
        }
        setObjectMeta((prev) => {
            prev = { ...prev };
            prev[objectName] = {
                instanceName,
                listLayout,
                formLayout,
                pkColumnNames,
                editModeColumnNames
            };
            return prev;
        });
        if (shouldLoadDataAfterInit) {
            let searchParamsStr = "";
            if (searchParams) searchParamsStr = searchParams.toString();
            if (!searchParamsStr) return;
            const pkDefsAndVars = urlStringToPk(searchParamsStr);
            setLoadDataAfterInit({
                objectName: params.layoutId,
                layoutColumns: [],
                otherColumns: [editModeColumnNames],
                prmDefs: pkDefsAndVars.defs,
                prmVals: pkDefsAndVars.vals,
                defVars: {
                    instanceName: instanceName
                }
            });
        }
    }, [params.layoutId, props.userToken, searchParams, serverUrl]);

    const getLoadedData = useCallback((objectName) => {
        return (data[objectName]) ? data[objectName] : [];
    }, [data]);

    const getRestrictions = useCallback((objectName) => {
        return (rowsRestrictions[objectName]) ? rowsRestrictions[objectName] : [];
    }, [rowsRestrictions]);

    const getLayout = useCallback(() => {
        return layout;
    }, [layout]);

    const getRowsRestrictions = useCallback((objectName, index) => {
        let rowRestriction = getRestrictions(objectName);
        if (rowRestriction.length === 0) return true;
        if (rowRestriction.length < index) return true;
        return rowRestriction[index]?.canBeUpdated;
    }, [getRestrictions]);


    const getChangedData = useCallback((objectName) => {
        return (changedData[objectName]) ? changedData[objectName] : [];
    }, [changedData]);

    const getDataIndex = useCallback((objectName, obj) => {
        // const pkColumnNames = objectMeta[objectName].pkColumnNames;

        const pkColumnNames = objectMeta?.[objectName]?.pkColumnNames || [];
        const data = getLoadedData(objectName);
        for (let i = 0; i < data.length; i++) {
            let found = true;
            // εδω θα κάνω αλλαγη  todo
            for (let j = 0; j < pkColumnNames.length; j++) {
                if (obj[pkColumnNames[j]] !== data[i][pkColumnNames[j]]
                    && (obj[pkColumnNames[j]] !== null && data[i][pkColumnNames[j]] !== null)) {
                    found = false;
                    break;
                };
            }
            if (found) return i;
        }
        return -1;
    }, [getLoadedData, objectMeta]);

    const getChangedDataIndex = useCallback((objectName, obj) => {
        //  const pkColumnNames = objectMeta[objectName].pkColumnNames;
        const pkColumnNames = objectMeta?.[objectName]?.pkColumnNames || [];
        const changedData = getChangedData(objectName);
        for (let i = 0; i < changedData.length; i++) {
            if (changedData[i].current === null) continue;
            let found = true;
            for (let j = 0; j < pkColumnNames.length; j++) {
                if (obj[pkColumnNames[j]] !== changedData[i].current[pkColumnNames[j]]
                    && (obj[pkColumnNames[j]] !== null && changedData[i].current[pkColumnNames[j]] !== null)) {
                    found = false;
                    break;
                };
            }
            if (found) return i;
        }
        return -1;
    }, [getChangedData, objectMeta]);

    const getAndCacheToServer = useCallback(async (objectName, obj) => {
        if (!objectMeta || !objectMeta[objectName]) return;
        const editModeColumnNames = objectMeta[objectName].editModeColumnNames;
        // const pkColumnNames = objectMeta[objectName].pkColumnNames;
        const pkColumnNames = objectMeta?.[objectName]?.pkColumnNames || [];
        const instanceName = objectMeta[objectName].instanceName;
        const prmDefsAndVals = createPkPrmDefsAndVals(obj, pkColumnNames);
        const requestBody = createGql(objectName, [], editModeColumnNames, prmDefsAndVals.defs, prmDefsAndVals.vals, { instanceName });
        let url = `${serverUrl}/api/cacheObject`;
        const result = await callApi('POST', url, props.userToken, requestBody);
        const objUnderEdit = {
            cacheKey: result.cacheKey,
            current: result.data[objectName][0],
            original: result.data[objectName][0],
            rowRestrictions: result.rowRestrictions,
            disabledFields: result.disabledFields,
            originalDisabledFields: { ...result.disabledFields }
        }
        setObjUnderEdit((prev) => {
            prev = JSON.parse(JSON.stringify(prev));
            prev[objectName] = objUnderEdit;
            return prev;
        });
    }, [createPkPrmDefsAndVals, objectMeta, props.userToken, serverUrl]);

    const cacheToServer = useCallback(async (objectName, currentObj, originalObj) => {
        const instanceName = objectMeta[objectName].instanceName;
        let url = `${serverUrl}/api/cacheObject/setToCache`;
        const requestBody = {
            objectName,
            instanceName,
            obj: currentObj
        }
        const result = await callApi('PUT', url, props.userToken, requestBody);
        const objUnderEdit = {
            cacheKey: result.cacheKey,
            current: currentObj,
            original: originalObj,
            rowRestrictions: result.rowRestrictions,
            disabledFields: result.disabledFields
        };
        setObjUnderEdit((prev) => {
            prev = JSON.parse(JSON.stringify(prev));
            prev[objectName] = objUnderEdit;
            return prev;
        });
    }, [objectMeta, props.userToken, serverUrl]);

    const beginEdit = useCallback(async (objectName, obj) => {
        try {
            setError(null);
            const changedDataIndex = getChangedDataIndex(objectName, obj);
            if (changedDataIndex === -1) {
                await getAndCacheToServer(objectName, obj);
            } else {
                await cacheToServer(objectName, obj, changedData[objectName][changedDataIndex].original);
            }
        } catch (err) {
            setError(err.message);
        }
    }, [cacheToServer, changedData, getAndCacheToServer, getChangedDataIndex]);

    const updateChangedData = useCallback((objectName) => {
        if (!objUnderEdit[objectName]) return;
        const current = objUnderEdit[objectName].current;
        const original = objUnderEdit[objectName].original;
        const listLayout = objectMeta[objectName].listLayout;
        const formLayout = objectMeta[objectName].formLayout;
        if (!objectsAreEqual(current, original)) {
            setData(prev => {
                prev = { ...prev };
                const dataIndex = getDataIndex(objectName, current);
                prev[objectName][dataIndex] = current;
                return prev;
            });
            setChangedData(prev => {
                prev = { ...prev };
                const changedDataIndex = getChangedDataIndex(objectName, current);
                const dataIndex = getDataIndex(objectName, current, listLayout, formLayout);
                if (changedDataIndex === -1) {
                    if (!prev[objectName]) prev[objectName] = [];
                    else prev[objectName] = [...prev[objectName]];
                    if (dataIndex !== -1 && prev[objectName].length > dataIndex) {
                        prev[objectName][dataIndex].current = current;
                    }
                    else if (dataIndex > 0 && prev[objectName].length === dataIndex) {
                        prev[objectName][dataIndex - 1].current = current;
                    }
                    else {
                        prev[objectName].push({
                            action: (dataIndex === -1) ? "insert" : "update",
                            original: original,
                            current: current
                        })
                    }
                } else {
                    prev[objectName][changedDataIndex].current = current;
                }
                return prev;
            });
        }
    }, [getChangedDataIndex, getDataIndex, objUnderEdit, objectMeta]);

    const onColumnChanged = useCallback(async (objectName, columnName, newValue) => {
        try {
            setError(null);
            if (!objUnderEdit[objectName]) return;
            const cacheKey = objUnderEdit[objectName].cacheKey;
            let disabledFields = objUnderEdit[objectName].disabledFields;
            if (!disabledFields) disabledFields = {};
            const obj = { ...objUnderEdit[objectName].current };
            let stateChanges = await callApi('POST', `${serverUrl}/api/layouts/columnChanged?cacheKey=${cacheKey}&columnName=${columnName}&newValue=${encodeURIComponent(newValue)}`);
            for (let i = 0; i < stateChanges.changedFields.length; i++) {
                let f = stateChanges.changedFields[i];
                setPropValue(obj, f.c, f.v);
            }
            for (let i = 0; i < stateChanges.fieldsToDisable.length; i++) {
                disabledFields[stateChanges.fieldsToDisable[i]] = true;
            }
            for (let i = 0; i < stateChanges.fieldsToEnable.length; i++) {
                disabledFields[stateChanges.fieldsToEnable[i]] = false;
            }
            setObjUnderEdit((prev) => {
                prev = { ...prev };
                prev[objectName].disabledFields = disabledFields;
                prev[objectName].current = obj;
                return prev;
            });
        } catch (err) {
            setError(err.message);
        }
    }, [objUnderEdit, serverUrl]);

    const getUnderEditValue = useCallback((objectName, columnName) => {
        if (!objUnderEdit[objectName]) return null;
        const current = objUnderEdit[objectName].current;
        return getPropValue(current, columnName);
    }, [objUnderEdit]);

    const endEdit = useCallback(async (objectName, shouldSaveAfterEndEdit, shouldBeginEditAfterEdit, objToEdit) => {
        try {
            if (!objUnderEdit[objectName] || !objUnderEdit[objectName].current) return;
            updateChangedData(objectName);
            let url = `${serverUrl}/api/cacheObject`;
            const requestBody = {
                query: "",
                variables: { cacheKey: objUnderEdit[objectName].cacheKey }
            }
            await callApi('DELETE', url, props.userToken, requestBody);
            setObjUnderEdit(prev => {
                prev = JSON.parse(JSON.stringify(prev));
                prev[objectName] = null;
                return prev;
            });
            if (shouldSaveAfterEndEdit) {
                let layoutColumns = [];
                let otherColumns = [[]];
                if (objectMeta[objectName].listLayout) {
                    layoutColumns = objectMeta[objectName].listLayout.columns;
                    otherColumns = [objectMeta[objectName].listLayout.additionalColumns, objectMeta[objectName].editModeColumnNames];
                } else {
                    otherColumns = [[objectMeta[objectName].editModeColumnNames]];
                }
                setSaveAfterEndEdit({
                    objectName, layoutColumns, otherColumns, currentObject: objUnderEdit[objectName].current
                });
            }
            if (shouldBeginEditAfterEdit) {
                setBeginEditAfterEdit({ objectName, objToEdit });
            }
        } catch (err) {
            setError(err.message);
        }
    }, [objUnderEdit, objectMeta, props.userToken, serverUrl, updateChangedData]);

    const insertNewRow = useCallback(async (objectName, dontCash, parentSelectedRow) => {
        try {
            setError(null);
            const editModeColumnNames = objectMeta[objectName].editModeColumnNames;
            const instanceName = objectMeta[objectName].instanceName;
            if (!dontCash) dontCash = false;
            const requestBody = createGql(objectName, [], editModeColumnNames, [], [], { createNewRow: true, dontCash, instanceName, parentSelectedRow });
            let url = `${serverUrl}/api/cacheObject`;
            const result = await callApi('POST', url, props.userToken, requestBody);
            if (dontCash === false) {
                const objUnderEdit = {
                    cacheKey: result.cacheKey,
                    current: result.data[objectName][0],
                    original: {},
                    rowRestrictions: result.rowRestrictions,
                    disabledFields: result.disabledFields
                }
                setObjUnderEdit((prev) => {
                    prev = JSON.parse(JSON.stringify(prev));
                    prev[objectName] = objUnderEdit;
                    return prev;
                });
            }
            setData(prev => {
                prev = { ...prev }
                if (!prev[objectName]) prev[objectName] = [];
                else prev[objectName] = [...prev[objectName]];
                prev[objectName].push(result.data[objectName][0]);
                return prev;
            });
            setChangedData(prev => {
                prev = { ...prev };
                if (!prev[objectName]) prev[objectName] = [];
                else prev[objectName] = [...prev[objectName]];
                prev[objectName].push({
                    action: "insert",
                    original: null,
                    current: result.data[objectName][0]
                });
                return prev;
            });
        } catch (err) {
            setError(err.message);
        }
    }, [objectMeta, props.userToken, serverUrl]);

    const moveCurrentEdit = useCallback(async (objectName, cnt) => {
        if (!objUnderEdit || !objUnderEdit[objectName] || !objUnderEdit[objectName].current) return;
        const dataIndex = getDataIndex(objectName, objUnderEdit[objectName].current);
        if (dataIndex === -1) return;
        if (dataIndex + cnt < 0 || dataIndex > getLoadedData(objectName).length - 1) return;
        await endEdit(objectName, false, true, getLoadedData(objectName)[dataIndex + cnt]);
    }, [endEdit, getDataIndex, getLoadedData, objUnderEdit]);

    const deleteRow = useCallback(async (objectName, obj, idx) => {
        if (obj) {
            idx = getDataIndex(obj);
            if (idx === -1) return;
        } else {
            if (idx === -1) return;
            obj = data[objectName][idx];
        }
        const changedDataIndex = getChangedDataIndex(objectName, obj);
        if (changedDataIndex !== -1 && changedData[objectName][changedDataIndex].action === 'insert') {
            setChangedData(prev => {
                prev = { ...prev };
                prev[objectName] = [...prev[objectName]];
                prev[objectName].splice(changedDataIndex, 1);
                return prev;
            });
        } else {
            const changedDataEntry = {
                action: 'delete',
                original: obj,
                current: null
            }
            setChangedData(prev => {
                prev = { ...prev };
                prev[objectName] = [...prev[objectName]];
                if (changedDataIndex === -1) {
                    prev[objectName].push(changedDataEntry);
                } else {
                    prev[objectName] = changedDataEntry;
                }
                return prev;
            });
        }
        setData(prev => {
            if (!prev[objectName]) return prev;
            prev = { ...prev }
            prev[objectName] = [...prev[objectName]];
            prev[objectName].splice(idx, 1);
            return prev;
        });
    }, [changedData, data, getChangedDataIndex, getDataIndex]);

    const saveData = useCallback(async (objectName, layoutColumns, otherColumns, shouldBeginEditAfterSave, currentObject) => {
        try {
            const instanceName = objectMeta[objectName].instanceName;
            let additionalColumns = [];
            otherColumns.forEach(colArray => additionalColumns = [...additionalColumns, ...(colArray) ? colArray : []]);
            const gqlCols = createGql("", layoutColumns, additionalColumns);
            const requestBody = {
                query: `mutation save${objectName}($obj: ${objectName}!){` +
                    `save${objectName}(${objectName}: $obj) ${gqlCols.query} }`,
                variables: {
                    "obj": {
                        instanceName: instanceName,
                        filterName: "default",
                        top: gridTop,
                        changedData: changedData[objectName]
                    }
                }
            };
            let url = `${serverUrl}/api/query`;
            const gqlResult = await callApi('POST', url, props.userToken, requestBody);
            const dataToMerge = gqlResult.data[objectName];
            const objData = [...data[objectName]];
            for (let i = 0; i < dataToMerge.length; i++) {
                const idx = getDataIndex(objectName, dataToMerge[i]);
                objData[idx] = dataToMerge[i];
            }
            setData(prev => {
                prev = { ...prev };
                prev[objectName] = objData;
                return prev;
            });
            setChangedData(prev => {
                prev = { ...prev };
                prev[objectName] = [];
                return prev;
            });
            if (shouldBeginEditAfterSave) {
                setBeginEditAfterSave({ objectName, currentObject });
            }
        } catch (err) {
            setError(err.message);
        }
    }, [changedData, data, getDataIndex, gridTop, objectMeta, props.userToken, serverUrl]);

    const undo = useCallback(async (objectName) => {
        const orginalObjData = [...originalData[objectName]];
        setData(prev => {
            prev = { ...prev };
            prev[objectName] = orginalObjData;
            return prev;
        });
        setChangedData(prev => {
            prev = { ...prev };
            prev[objectName] = [];
            return prev;
        });
        if (objUnderEdit[objectName]) {
            setObjUnderEdit((prev) => {
                prev = { ...prev };
                prev[objectName] = {
                    ...prev[objectName],
                    current: prev[objectName].original,
                    disabledFields: { ...prev[objectName].originalDisabledFields }
                }
                return prev;
            });
        }
    }, [objUnderEdit, originalData]);

    const requestData = useCallback(async (objectName, layoutColumns, otherColumns, prmDefs, prmVals, defVars) => {
        setError(null);
        try {
            let additionalColumns = [];
            otherColumns.forEach(colArray => additionalColumns = [...additionalColumns, ...(colArray) ? colArray : []]);
            const requestBody = createGql(objectName, layoutColumns, additionalColumns, prmDefs, prmVals, defVars);
            let url = `${serverUrl}/api/query`;
            const gqlResult = await callApi('POST', url, props.userToken, requestBody);
            // return gqlResult.data[objectName];
            return gqlResult;
        } catch (err) {
            setError(err.message);
            return [];
        }
    }, [props.userToken, serverUrl]);

    const loadData = useCallback(async (objectName, layoutColumns, otherColumns, prmDefs, prmVals, defVars, shouldBeginEditAfterLoad, onDataLoaded) => {
        setError(null);
        let requestdata = await requestData(objectName, layoutColumns, otherColumns, prmDefs, prmVals, defVars);

        let objectData = requestdata.data[objectName];
        let restrictions = requestdata.rowsRestrictions[objectName];
        setRowsRestrictions(prev => {
            prev = { ...prev };
            prev[objectName] = restrictions;
            return prev;
        });
        setData(prev => {
            prev = { ...prev };
            prev[objectName] = objectData;
            return prev;
        });
        setOriginalData(prev => {
            prev = { ...prev };
            prev[objectName] = [...objectData];
            return prev;
        });
        setChangedData(prev => {
            prev = { ...prev };
            prev[objectName] = [];
            return prev;
        });
        if (shouldBeginEditAfterLoad) {
            setBeginEditAfterLoad({ objectName, objectData });
        }
        if (onDataLoaded) {
            onDataLoaded(objectData);
        }
    }, [requestData]);

    const clearData = useCallback((objectName) => {
        if (!objectName) {
            setData({});
            setOriginalData({});
            setObjectMeta({});
            setObjUnderEdit({});
            setChangedData({});
            // setRowsRestrictions({})
        } else {
            const objectData = [];
            setData(prev => {
                prev = { ...prev };
                prev[objectName] = objectData;
                return prev;
            });
            setOriginalData(prev => {
                prev = { ...prev };
                prev[objectName] = [...objectData];
                return prev;
            });
            setChangedData(prev => {
                prev = { ...prev };
                prev[objectName] = [];
                return prev;
            });
        }
    }, []);

    const hasChanges = useCallback((objectName) => {
        if (changedData[objectName] && changedData[objectName].length) return true;
        else return false;
    }, [changedData]);

    const getFormLayout = useCallback((objectName) => {
        if (!objectMeta || !objectMeta[objectName]) return null;
        return objectMeta[objectName].formLayout;
    }, [objectMeta]);

    const getObjUnderEdit = useCallback((objectName) => {
        if (!objUnderEdit || !objUnderEdit[objectName]) return null;
        return objUnderEdit[objectName];
    }, [objUnderEdit]);

    //dataContext gets, sets or updates data.
    //Is must not be used as a dependency on useEffect.
    //It is usefull for event handling and for rendering
    const dataContext = useMemo(() => ({
        getLoadedData,
        beginEdit,
        onColumnChanged,
        endEdit,
        getUnderEditValue,
        insertNewRow,
        deleteRow,
        saveData,
        hasChanges,
        undo,
        getFormLayout,
        getObjUnderEdit,
        moveCurrentEdit,
        getRowsRestrictions,
        getLayout
    }), [getLoadedData, beginEdit, onColumnChanged, endEdit, getUnderEditValue, insertNewRow, deleteRow, saveData, hasChanges, undo, getFormLayout, getObjUnderEdit, moveCurrentEdit, getRowsRestrictions, getLayout]);

    //dataEngine only sets or updates data, but must not get (return)!!!
    //It is used as a dependency on useEffect, so changes on data must no affect dataEngine
    const dataEngine = useMemo(() => ({
        requestData,
        loadData,
        clearData,
        initObjectMeta
    }), [clearData, initObjectMeta, loadData, requestData]);

    useEffect(() => {
        async function initFormFromUrl() {
            if (props.componentType !== 'form') return;
            let url = `${serverUrl}/api/layouts/form/${params.layoutId}`;
            searchParams.get('instanceName')
            if (searchParams.get('instanceName')) url += `?instanceName=${searchParams.get('instanceName')}`;
            const formLayout = await callApi('GET', url, props.userToken);
            initObjectMeta(params.layoutId, searchParams.get('instanceName'), null, formLayout, true);
        }
        initFormFromUrl();
    }, [initObjectMeta, params.layoutId, props.componentType, props.userToken, searchParams, serverUrl]);

    useEffect(() => {
        async function doIt() {
            if (!loadDataAfterInit) return;
            await loadData(loadDataAfterInit.objectName, loadDataAfterInit.layoutColumns, loadDataAfterInit.otherColumns,
                loadDataAfterInit.prmDefs, loadDataAfterInit.prmVals, loadDataAfterInit.defVars, true);
            setLoadDataAfterInit(null);
        }
        doIt();
    }, [loadData, loadDataAfterInit]);

    useEffect(() => {
        async function doIt() {
            if (!beginEditAfterLoad) return;
            if (beginEditAfterLoad.objectData.length) {
                await beginEdit(beginEditAfterLoad.objectName, beginEditAfterLoad.objectData[0]);
            }
            setBeginEditAfterLoad(null);
        }
        doIt();
    }, [beginEdit, beginEditAfterLoad]);

    useEffect(() => {
        async function doIt() {
            if (!saveAfterEndEdit) return;
            saveData(saveAfterEndEdit.objectName, saveAfterEndEdit.layoutColumns, saveAfterEndEdit.otherColumns, true, saveAfterEndEdit.currentObject);
            setSaveAfterEndEdit(null);
        }
        doIt();
    }, [saveAfterEndEdit, saveData]);

    useEffect(() => {
        async function doIt() {
            if (!beginEditAfterSave) return;
            if (beginEditAfterSave.currentObject) {
                await beginEdit(beginEditAfterSave.objectName, beginEditAfterSave.currentObject);
            }
            setBeginEditAfterSave(null);
        }
        doIt();
    }, [beginEdit, beginEditAfterSave]);

    useEffect(() => {
        async function doIt() {
            if (!beginEditAfterEdit) return;
            if (beginEditAfterEdit.objToEdit) {
                await beginEdit(beginEditAfterEdit.objectName, beginEditAfterEdit.objToEdit);
            }
            setBeginEditAfterSave(null);
        }
        doIt();
    }, [beginEdit, beginEditAfterEdit]);

    useEffect(() => {
        if (!params.layoutId && !props.layoutId) return null;
        const layoutId = params.layoutId ? params.layoutId : props.layoutId;
        setLayout(layoutId);
        let urlSuffix = '';
        if (searchParams && searchParams.toString()) {
            urlSuffix = `?${searchParams.toString()}`;
        }
        let component = null;
        if (props.componentType === 'list') {
            component = <CrudList dataEngine={dataEngine} dataContext={dataContext} userToken="" layoutUrl={`api/layouts/list/${layoutId}${urlSuffix}`} instanceName={searchParams.get('instanceName')} rowSelected={props.listRowSelected} />
        } else if (props.componentType === 'dlist') { //dynmamic list
            component = <CompositeLayout dataEngine={dataEngine} dataContext={dataContext} userToken="" masterListLayoutId={`${layoutId}`} masterListInstanceName={searchParams.get('instanceName')} />
        } else if (props.componentType === 'composite') {
            component = <CompositeLayout dataEngine={dataEngine} dataContext={dataContext} userToken="" layoutUrl={`api/layouts/composite/${layoutId}`} />
        } else if (props.componentType === 'form') {
            if (objectMeta && objectMeta[layoutId]) {
                let obj = {}
                if (objUnderEdit && objUnderEdit[layoutId]) {
                    obj = objUnderEdit[layoutId];
                }
                component = <CrudForm
                    dataContext={dataContext}
                    userToken={props.userToken}
                    layout={objectMeta[layoutId].formLayout}
                    instanceName={searchParams.get('instanceName')}
                    objUnderEdit={obj}
                    active={true} isStandAlone={true} />
            }
        } else {
            component = <>Unsupported component type {props.componentType}</>
        }
        setComponent(component);
    }, [dataContext, dataEngine, objUnderEdit, objectMeta, params.layoutId, props.componentType, props.userToken, searchParams, props.layoutId, props.listRowSelected]);

    return (
        <>
            <ErrorAlert message={error} setMessage={setError} language={language}/>
            {component}
        </>
    );
}
